import { Input, InputGroup, toaster } from "rsuite";
import React, { useEffect, useState } from "react";
import { ElectronicClockApi } from "../../../../../../services/ElectronicClock.api";
import NotificationFactory from "../../../../../../shared/Notifications";
import { SharedApi } from "../../../../../../services/Shared.api";
import {TfiSaveIcon} from "../../../../../../models/Icons.model";
import {ElectronicClockData, ElectronicClockInfoSectionProps} from "../ElectronicClock.model";
import DeviceParameterSyncButton from "../../../../../shared/DeviceParameterSyncButton/DeviceParameterSyncButton";
import {ExternalDeviceType} from "../../../ExternalDevices.model";
import {ExternalDevicesUtils} from "../../../ExternalDevices.utils";

const ElectronicClockInfoSection = ({ id, isAdmin }: ElectronicClockInfoSectionProps) => {
  const [loading, setLoading] = useState(true);
  const [electronicClockData, setElectronicClockData] = useState<ElectronicClockData>({});

  useEffect(() => {
    getElectronicClockData().then();
  }, [id]);

  const getElectronicClockData = async () => {
    setLoading(true);
    try {
      const result = await ElectronicClockApi.getElectronicClockData(id as string);

      setElectronicClockData(result as ElectronicClockData);
    } catch (error) {
      toaster.push(
        NotificationFactory.error("Ошибка"),
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  const onChangeArticle = (value: string) => {
    setElectronicClockData((prev) => ({
      ...prev,
      article: value
    }));
  };

  const onChangeModel = (value: string) => {
    setElectronicClockData((prev) => ({
      ...prev,
      model: value
    }));
  };

  const onChangePlace = (value: string) => {
    setElectronicClockData((prev) => ({
      ...prev,
      place: value
    }));
  };

  const onSaveArticle = async () => {
    setLoading(true);
    try {
      await SharedApi.updateArticle(id as string, electronicClockData?.article ?? "");
      toaster.push(
        NotificationFactory.success("Артикул обновлен"),
        { placement: "topEnd" }
      );
      await getElectronicClockData();
    } catch (e) {
      console.error("Ошибка при сохранении артикула:", e);
      toaster.push(
        NotificationFactory.error("Не удалось обновить артикул"),
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  const onSaveModel = async () => {
    setLoading(true);
    try {
      await SharedApi.updateModel(id as string, electronicClockData?.model ?? "");
      toaster.push(
        NotificationFactory.success("Модель обновлена"),
        { placement: "topEnd" }
      );
      await getElectronicClockData();
    } catch (e) {
      console.error("Ошибка при сохранении модели:", e);
      toaster.push(
        NotificationFactory.error("Ошибка при сохранении модели"),
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  const onSavePlace = async () => {
    setLoading(true);
    try {
      await SharedApi.updatePlace(id as string, electronicClockData?.place ?? "");
      toaster.push(
        NotificationFactory.success("Место установки обновлено"),
        { placement: "topEnd" }
      );
      await getElectronicClockData();
    } catch (e) {
      console.error("Ошибка при сохранении места:", e);
      toaster.push(
        NotificationFactory.error("Ошибка при сохранении места"),
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section>
        <div className="row">
          <div className="col-md-12">
            <div className="mb-3">
              <InputGroup>
                <InputGroup.Addon>Артикул</InputGroup.Addon>
                <Input
                  size="md"
                  value={electronicClockData?.article || ""}
                  onChange={onChangeArticle}
                />
                {isAdmin && (
                  <InputGroup.Addon>
                    <TfiSaveIcon
                      style={{ cursor: "pointer" }}
                      onClick={onSaveArticle}
                      className={loading ? "loading" : ""}
                    />
                  </InputGroup.Addon>
                )}
              </InputGroup>
            </div>

            <div className="mb-3">
              <InputGroup>
                <InputGroup.Addon>Дата производства</InputGroup.Addon>
                <Input
                  size="md"
                  value={electronicClockData?.fabricDate || ""}
                />
              </InputGroup>
            </div>

            <div className="mb-3">
              <InputGroup>
                <InputGroup.Addon>Версия прошивки</InputGroup.Addon>
                <Input
                  size="md"
                  value={electronicClockData?.firmwareVersion || ""}
                />
                {isAdmin && (
                  <DeviceParameterSyncButton
                    deviceId={id}
                    parameter="FirmwareVersion"
                    onSynced={getElectronicClockData}
                  />
                )}
              </InputGroup>
            </div>
          </div>

          <div className="col-md-12">
            <div className="mb-3">
              <InputGroup>
                <InputGroup.Addon>Модель</InputGroup.Addon>
                <Input
                  size="md"
                  value={electronicClockData?.model || ""}
                  onChange={onChangeModel}
                />
                {isAdmin && (
                  <InputGroup.Addon>
                    <TfiSaveIcon
                      style={{ cursor: "pointer" }}
                      onClick={onSaveModel}
                      className={loading ? "loading" : ""}
                    />
                  </InputGroup.Addon>
                )}
              </InputGroup>
            </div>
            <div className="mb-3">
              <InputGroup>
                <InputGroup.Addon>Серийный номер</InputGroup.Addon>
                <Input
                  size="md"
                  value={ExternalDevicesUtils.formatDeviceSerialNumber(electronicClockData?.serialNumber, ExternalDeviceType.ElectronicClock)}
                />
              </InputGroup>
            </div>
            <div className="mb-3">
              <InputGroup>
                <InputGroup.Addon>Место установки</InputGroup.Addon>
                <Input
                  size="md"
                  value={electronicClockData?.place || ""}
                  onChange={onChangePlace}
                />
                {isAdmin && (
                  <DeviceParameterSyncButton
                    deviceId={id}
                    parameter="Place"
                    onSynced={getElectronicClockData}
                  />
                )}
                <InputGroup.Addon>
                  <TfiSaveIcon
                    style={{ cursor: "pointer" }}
                    onClick={onSavePlace}
                    className={loading ? "loading" : ""}
                  />
                </InputGroup.Addon>
              </InputGroup>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ElectronicClockInfoSection;
